import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRobot, faArrowRightLong } from "@fortawesome/free-solid-svg-icons";
import { jsPDF } from "jspdf";

export default function JobAutomationReport() {
  const [data, setData] = useState({ role: "", tasks: "", tools: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handle = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const generate = (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    const t = data.tasks.toLowerCase();
    const tool = data.tools.toLowerCase();

    const repetitionKeywords = ["daily", "upload", "copy", "email", "assign", "categorize", "update", "track", "crm", "sheet", "routine", "report", "entry", "schedule"];
    const decisionKeywords = ["approve", "review", "analyze", "strategy", "planning", "judgment", "qualify", "interpret", "negotiate"];
    const communicationKeywords = ["email", "chat", "support", "respond", "customer", "inbox", "follow-up"];

    const repCount = repetitionKeywords.filter((k) => t.includes(k)).length;
    const decCount = decisionKeywords.filter((k) => t.includes(k)).length;
    const commCount = communicationKeywords.filter((k) => t.includes(k)).length;

    const repeatability = Math.min(100, repCount * 12 + 20);
    const complexity = Math.min(100, decCount * 15 + 10);
    const communication = Math.min(100, commCount * 14 + 10);

    const base = 0.55 * repeatability + 0.25 * (100 - complexity) + 0.20 * (communication > 60 ? communication * 0.7 : communication * 0.9);
    const score = Math.max(35, Math.min(95, Math.floor(base)));

    const blueprint = [
      "AI Assistant for email triage, summaries, tagging, and drafting responses.",
      "Workflow automation for CRM syncing, lead updates, and follow-up triggers.",
      "RPA-style actions for repetitive spreadsheet operations.",
      "Automated reporting pipeline for weekly insights and dashboards.",
      "Custom chatbot or intake bot for front-line filtering."
    ];

    const refinedBlueprint = blueprint.sort(() => 0.5 - Math.random()).slice(0, 3);

    const barriers = [
      "High need for contextual judgment.",
      "Complex multi-branch workflows.",
      "Unstructured communication requiring nuance.",
      "Role has high interpersonal dependency.",
      "Data fragmentation across tools."
    ];
    const detectedBarriers = barriers.sort(() => 0.5 - Math.random()).slice(0, 2);

    const toolHints = [];
    if (tool.includes("sheets")) toolHints.push("Automate spreadsheet operations with triggers, formulas, and Make/Zapier pipelines.");
    if (tool.includes("slack")) toolHints.push("Create automated Slack alerts, summaries, and workflow-driven notifications.");
    if (tool.includes("crm")) toolHints.push("Auto-sync CRM fields, status updates, and enrichment.");
    if (tool.includes("notion")) toolHints.push("Automate task creation, tagging, and meeting summaries.");
    if (tool.includes("outlook") || tool.includes("gmail")) toolHints.push("Inbox automation for categorization, templating, and priority routing.");
    if (!toolHints.length) toolHints.push("General automation opportunities detected across standard tools.");

    const rawLines = data.tasks
    .split('\n')
    .map(l => l.trim())
    .filter(l => l.length > 0);
  
  const taskLikePatterns = [
    /^[A-Z]/,
    /^- /,
    /^\d+\./,
    /(manage|handle|process|update|organize|respond|monitor|analyze|prepare|upload|coordinate|review|draft|conduct|schedule|track)/i
  ];
  
  const cleanedTasks = rawLines.filter(line =>
    taskLikePatterns.some(r => r.test(line))
  );
  
  const taskList = cleanedTasks.map(task => {
    const repTaskCount = repetitionKeywords.filter(k => task.toLowerCase().includes(k)).length;
    const decTaskCount = decisionKeywords.filter(k => task.toLowerCase().includes(k)).length;
    const commTaskCount = communicationKeywords.filter(k => task.toLowerCase().includes(k)).length;
  
    const taskRepeatability = Math.min(100, repTaskCount * 12 + 15);
    const taskComplexity = Math.min(100, decTaskCount * 15 + 8);
    const taskCommunication = Math.min(100, commTaskCount * 14 + 8);
  
    const taskBase =
      0.55 * taskRepeatability +
      0.25 * (100 - taskComplexity) +
      0.20 * (taskCommunication > 60 ? taskCommunication * 0.7 : taskCommunication * 0.9);
  
    const taskScore = Math.max(30, Math.min(95, Math.floor(taskBase)));
  
    return { task, taskScore };
  });
  

    const overallSavings = taskList.reduce((acc, task) => acc + task.taskScore, 0) / taskList.length;

    setTimeout(() => {
      setResult({
        score,
        repeatability,
        complexity,
        communication,
        summary: `An estimated ${score}% of this role can be automated using AI assistants, workflow automation, and data pipelines.`,
        blueprint: refinedBlueprint,
        barriers: detectedBarriers,
        toolHints,
        taskList,
        overallSavings,
      });
      setLoading(false);
    }, 1000);
  };

  const downloadReport = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 40;
    const maxWidth = pageWidth - margin * 2;
    const pageHeight = doc.internal.pageSize.getHeight();
  
    let y = 40;
  
    const addText = (text, size = 12, indent = 0, spacing = 18) => {
      doc.setFontSize(size);
      const lines = doc.splitTextToSize(text, maxWidth - indent);
  
      lines.forEach(line => {
        if (y > pageHeight - 40) {
          doc.addPage();
          y = 40;
        }
        doc.text(line, margin + indent, y);
        y += spacing;
      });
    };
  
    addText("Job Automation Report", 20, 0, 30);
    addText(`Role: ${data.role}`, 12);
    addText(`Overall Automation Score: ${result.score}%`, 12);
    addText(`Estimated Savings: ${Math.round(result.overallSavings)}%`, 12, 0, 25);
  
    addText("Automation Blueprint", 14, 0, 25);
    result.blueprint.forEach(b => addText(`• ${b}`, 11, 15));
  
    addText("Task Breakdown", 14, 0, 30);
    result.taskList.forEach(t => {
      addText(t.task, 12, 15);
      addText(`Automation Potential: ${t.taskScore}%`, 10, 25, 22);
    });
  
    doc.save("job-automation-report.pdf");
  };  

  return (
    <>
      <Helmet>
        <title>Job Automation Report</title>
      </Helmet>

      <section className="relative overflow-hidden" style={{ backgroundImage: 'url("./assets/consul.png")' }}>
        <div className="absolute inset-0 bg-primary opacity-80" />
        <div className="relative max-w-5xl mx-auto px-6 py-24 text-white">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight">
              Job Automation <span className="text-blue-300">Report</span>
            </h1>
            <p className="mt-5 text-lg md:text-xl text-slate-200">A deeper, multi-factor automation analysis based on your tasks and tools.</p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 gap-10">
          <form onSubmit={generate} className="p-8 rounded-3xl border border-slate-200 bg-white shadow">
            <div className="grid gap-5">
              <input
                name="role"
                value={data.role}
                onChange={handle}
                placeholder="Your Job Role"
                required
                className="w-full px-4 py-3 rounded-xl border border-slate-300"
              />
              <textarea
                name="tasks"
                value={data.tasks}
                onChange={handle}
                placeholder="List your daily tasks"
                required
                className="w-full px-4 py-3 rounded-xl border border-slate-300 h-32"
              />
              <textarea
                name="tools"
                value={data.tools}
                onChange={handle}
                placeholder="Tools you use"
                className="w-full px-4 py-3 rounded-xl border border-slate-300 h-24"
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-4 rounded-xl bg-primary text-white font-semibold flex items-center justify-center gap-2"
              >
                {loading ? "Analyzing…" : <>Generate Report <FontAwesomeIcon icon={faArrowRightLong} /></>}
              </button>
            </div>
          </form>

          <div className="p-8 rounded-3xl border border-slate-200 bg-white shadow">
            {!result && (
              <div className="h-full flex flex-col items-center justify-center text-center text-slate-500">
                <FontAwesomeIcon icon={faRobot} className="text-5xl mb-4 text-blue-600" />
                <p className="text-lg">Your automation analysis will appear here.</p>
              </div>
            )}

            {result && (
              <div className="space-y-7">
                <div className="text-6xl font-extrabold text-secondary">{result.score}%</div>
                <div className="text-slate-600 text-lg">{result.summary}</div>

                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 text-center">
                    <div className="text-xl font-bold">{result.repeatability}%</div>
                    <div className="text-xs text-slate-500">Repeatability</div>
                  </div>
                  <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 text-center">
                    <div className="text-xl font-bold">{result.complexity}%</div>
                    <div className="text-xs text-slate-500">Decision Complexity</div>
                  </div>
                  <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 text-center">
                    <div className="text-xl font-bold">{result.communication}%</div>
                    <div className="text-xs text-slate-500">Communication Load</div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="font-bold text-secondary text-lg">Automation Blueprint</div>
                  {result.blueprint.map((i, x) => (
                    <div key={x} className="p-4 rounded-2xl bg-slate-50 border border-slate-200">{i}</div>
                  ))}
                </div>

                <div className="space-y-3">
                  <div className="font-bold text-secondary text-lg">Potential Barriers</div>
                  {result.barriers.map((i, x) => (
                    <div key={x} className="p-4 rounded-2xl bg-slate-50 border border-slate-200">{i}</div>
                  ))}
                </div>

                <div className="space-y-3">
                  <div className="font-bold text-secondary text-lg">Tool-Specific Opportunities</div>
                  {result.toolHints.map((i, x) => (
                    <div key={x} className="p-4 rounded-2xl bg-slate-50 border border-slate-200">{i}</div>
                  ))}
                </div>

                <div className="mt-6">
                  <a href="/automation" className="inline-block px-6 py-3 rounded-full bg-primary text-white font-semibold">
                    Talk to an Automation Expert
                  </a>
                </div>

                <div className="mt-10">
                  <h3 className="text-2xl font-bold text-secondary mb-4">Task-by-Task Automation Breakdown</h3>
                  {result.taskList.map((task, index) => (
                    <div key={index} className="bg-slate-50 p-4 mb-4 rounded-xl shadow-md">
                      <div className="font-semibold text-lg">{task.task}</div>
                      <div className="text-sm text-gray-600">Automation Potential: {task.taskScore}%</div>
                      <div className="mt-2 text-sm text-gray-500">
                        Recommended automation: {task.taskScore > 60 ? 'High' : 'Low'}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 text-center">
                  <button onClick={downloadReport} className="inline-block px-6 py-3 bg-green-500 text-white font-semibold rounded-full">
                    Download Report
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
